import { FunctionComponent, useEffect, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-regular-svg-icons";

import { Autocomplete, TextField } from "@mui/material";

import { LinkedinParams } from "@/pages/Private/pages/LiPeople/components/LinkedinTagAutocomplete";
import { useSearch } from "@/utils/useSearch";
import { useLazyGetFilterByPropertyQuery } from "@/pages/Private/redux/list/list.api";

import { Information } from "../Information/Information";
import { Icon } from "../Icon/Icon";

export interface MultiAutoCompleteApiItem {
	id: string;
	title: string;
	icon?: JSX.Element;
}

export interface MultiAutoCompleteApiProps {
	handleSelect: (value?: MultiAutoCompleteApiItem[]) => void;
	selectedItems: MultiAutoCompleteApiItem[];
	label?: string;
	testId?: string;
	information?: string | JSX.Element;
	placeholder?: string;
	noData?: boolean;
	propertyName: string;
	preload?: boolean;
	disabled?: boolean;
	stayOn?: boolean;
}

export const MultiAutoCompleteApi: FunctionComponent<MultiAutoCompleteApiProps> = ({
	handleSelect,
	selectedItems,
	stayOn = false,
	label,
	information,
	placeholder,
	propertyName,
	preload = false,
	noData = false,
	disabled = false,
}) => {
	const [jobLevelOptions, setJobLevelOptions] = useState<MultiAutoCompleteApiItem[]>([]);

	const { query, searchValue, setSearchValue } = useSearch();

	const [fetchTags, { data: fetchTagsData, isLoading: fetchTagsIsLoading }] =
		useLazyGetFilterByPropertyQuery();

	useEffect(() => {
		if (noData) {
			if (!stayOn) {
				setJobLevelOptions([]);
			}

			return;
		}

		if (query || preload) {
			fetchTags({ propertyName, searchTerm: query }).then((results) => {
				setJobLevelOptions(
					(results?.data?.data?.map((r) => ({
						title: r.value,
						id: r.value,
					})) || []) as unknown as MultiAutoCompleteApiItem[]
				);
			});
		}
	}, [propertyName, query, fetchTags, preload, noData]);

	return (
		<div className="relative  w-full">
			{label && (
				<label className="flex flex-row items-center block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
					{information && (
						<span className="ml-1">
							<Information information={information} />
						</span>
					)}
				</label>
			)}

			<Autocomplete
				multiple
				disableCloseOnSelect={stayOn}
				clearOnBlur={true}
				disabled={disabled}
				getOptionLabel={(option) => option.title}
				inputValue={searchValue || ""}
				isOptionEqualToValue={(option, currentValue) => {
					if (currentValue.title === "" && currentValue.id === "") {
						return true;
					}

					return option.id === currentValue.id;
				}}
				loading={fetchTagsIsLoading}
				loadingText="Searching..."
				noOptionsText={
					searchValue && searchValue.length > 0 ? "Typing..." : "Start typing to search"
				}
				options={[{ id: searchValue || "", title: searchValue || "" }, ...jobLevelOptions]}
				popupIcon={<Icon className="text-gray-500 h-[24px] w-[15px]" icon={faChevronDown} />}
				renderInput={(params) => (
					<TextField
						{...params}
						label={placeholder || "Select an item"}
						placeholder={placeholder || "Select an item"}
						sx={{
							width: "100%",
							minHeight: 48,
							marginBottom: 1,
							"& .MuiFilledInput-root": {
								backgroundColor: "white",
								minHeight: 48,
								marginBottom: 0,
								borderRadius: 1.5,
								paddingTop: "10px",
								paddingBottom: "10px !important",
								paddingLeft: 1.5,
								"& .MuiChip-root": {
									backgroundColor: "#EAEEFF",
									color: "#1D3DF2",
								},
							},
							"& .MuiFilledInput-root:hover": {
								backgroundColor: "white",
							},
							"& .MuiInputLabel-root": {
								color: "#9CA3AF",
								fontSize: "16px",
								marginTop: "-4px",
							},
							"& .MuiInputLabel-root.MuiInputLabel-shrink": {
								display: "none",
							},
						}}
					/>
				)}
				size="small"
				sx={{
					width: "100%",
					marginBottom: 0,
					"& .MuiFormControl-root": {
						marginBottom: 0,
					},
				}}
				value={selectedItems || []}
				onBlur={(e) => {
					e.preventDefault();
					e.stopPropagation();
				}}
				onChange={(event, value) => {
					handleSelect(value || []);
					setSearchValue("");
				}}
				onInputChange={(event, newValue, reason) => {
					if (reason === "input") {
						setSearchValue(newValue);
					}
				}}
			/>
		</div>
	);
};
